import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Package, User as UserIcon } from "lucide-react";
import { useCustomer, withAuth } from "../lib/CustomerContext";
import api from "../lib/api";
import SEO from "../lib/SEO";

export default function Account() {
    const { customer, loading, logout, refresh } = useCustomer();
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", phone: "", default_address: "" });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!loading && !customer) navigate("/login?next=/account");
    }, [customer, loading, navigate]);

    useEffect(() => {
        if (customer) {
            setForm({
                name: customer.name || "",
                phone: customer.phone || "",
                default_address: customer.default_address || "",
            });
        }
    }, [customer]);

    const save = async (e) => {
        e.preventDefault();
        if (saving) return;
        setSaving(true);
        try {
            await api.put("/customer/me", form, withAuth());
            toast.success("Details saved");
            refresh();
        } catch {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    const doLogout = () => {
        logout();
        toast.success("You've been logged out");
        navigate("/");
    };

    if (loading || !customer) {
        return <main className="pt-32 pb-20 text-center text-ink-soft">Loading…</main>;
    }

    return (
        <main data-testid="account-page" className="pt-24 md:pt-[10rem] pb-20 bg-[#FFF7FB] min-h-[70vh]">
            <SEO title="My Account — Cake Central" path="/account" />
            <div className="max-w-3xl mx-auto px-4 sm:px-6">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="font-display text-4xl md:text-5xl text-ink leading-none">
                            Hi, {customer.name.split(" ")[0]}
                        </h1>
                        <p className="text-sm text-ink-soft mt-2">{customer.email}</p>
                    </div>
                    <button
                        onClick={doLogout}
                        className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-ink-soft hover:text-blush"
                        data-testid="account-logout"
                    >
                        <LogOut size={14} /> Log out
                    </button>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-8">
                    <Link
                        to="/account/orders"
                        className="bg-white border border-[#EAE3DE] p-5 hover:border-blush transition-colors flex items-center gap-4"
                        data-testid="account-orders-link"
                    >
                        <Package size={22} className="text-blush shrink-0" />
                        <div>
                            <p className="font-display text-lg">My orders</p>
                            <p className="text-xs text-ink-soft">View past orders and track status</p>
                        </div>
                    </Link>
                    <div className="bg-white border border-[#EAE3DE] p-5 flex items-center gap-4">
                        <UserIcon size={22} className="text-blush shrink-0" />
                        <div>
                            <p className="font-display text-lg">Account details</p>
                            <p className="text-xs text-ink-soft">Update your name, phone, and address</p>
                        </div>
                    </div>
                </div>

                <form onSubmit={save} className="bg-white border border-[#EAE3DE] p-6 space-y-4">
                    <h2 className="font-display text-xl mb-2">Your details</h2>
                    <Field label="Full Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                    <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} type="tel" />
                    <Field label="Default Delivery Address" value={form.default_address} onChange={(v) => setForm({ ...form, default_address: v })} textarea />
                    <button
                        type="submit"
                        disabled={saving}
                        className="btn-pink disabled:opacity-60"
                        data-testid="account-save"
                    >
                        {saving ? "Saving…" : "Save changes"}
                    </button>
                </form>
            </div>
        </main>
    );
}

function Field({ label, value, onChange, type = "text", textarea = false }) {
    const cls = "w-full px-4 py-3 border border-[#EAE3DE] focus:outline-none focus:border-blush";
    return (
        <div>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">{label}</label>
            {textarea ? (
                <textarea rows={2} value={value} onChange={(e) => onChange(e.target.value)} className={cls + " resize-none"} />
            ) : (
                <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className={cls} />
            )}
        </div>
    );
}
